import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Edit3, X, Save, AlertCircle, CheckCircle } from 'lucide-react';

interface DomainInfo {
  id: number;
  tenant_id: number;
  domain_name: string;
  is_primary: boolean;
  config: any;
  created_at: string;
  updated_at: string;
}

interface EditDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  domain: DomainInfo | null;
  onDomainUpdated: () => void;
}

interface FormData {
  domain_name: string;
  is_primary: boolean;
  primary_color: string;
  secondary_color: string;
  logo_url: string;
  icon_url: string;
  bg_url: string;
  phone: string;
  email: string;
  address: string;
}

export const EditDomainModal: React.FC<EditDomainModalProps> = ({
  isOpen,
  onClose,
  domain,
  onDomainUpdated
}) => {
  const [formData, setFormData] = useState<FormData>({
    domain_name: '',
    is_primary: false,
    primary_color: '',
    secondary_color: '',
    logo_url: '',
    icon_url: '',
    bg_url: '',
    phone: '',
    email: '',
    address: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Charger les données du domaine
  useEffect(() => {
    if (domain && isOpen) {
      const config = domain.config || {};
      setFormData({
        domain_name: domain.domain_name,
        is_primary: domain.is_primary,
        primary_color: config.colors?.primary || '',
        secondary_color: config.colors?.secondary || '',
        logo_url: config.logo || '',
        icon_url: config.icon || '',
        bg_url: config.bg || '',
        phone: config.phone || '',
        email: config.email || '',
        address: config.address || ''
      });
      setError('');
      setSuccess('');
    }
  }, [domain, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.domain_name.trim()) {
      setError('Le nom de domaine est requis');
      return false;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Format d\'email invalide');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!domain || !validateForm()) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Construire la configuration du domaine
      const domainConfig: any = {};

      // Ajouter les couleurs si spécifiées
      if (formData.primary_color || formData.secondary_color) {
        domainConfig.colors = {};
        if (formData.primary_color) domainConfig.colors.primary = formData.primary_color;
        if (formData.secondary_color) domainConfig.colors.secondary = formData.secondary_color;
      }

      // Ajouter les médias si spécifiés
      if (formData.logo_url) domainConfig.logo = formData.logo_url;
      if (formData.icon_url) domainConfig.icon = formData.icon_url;
      if (formData.bg_url) domainConfig.bg = formData.bg_url;

      // Ajouter les informations de contact si spécifiées
      if (formData.phone) domainConfig.phone = formData.phone;
      if (formData.email) domainConfig.email = formData.email;
      if (formData.address) domainConfig.address = formData.address;

      // Mettre à jour le domaine via RPC
      const { data: success, error: updateError } = await supabase
        .rpc('update_tenant_domain', {
          domain_id_param: domain.id,
          domain_name_param: formData.domain_name.toLowerCase().trim(),
          is_primary_param: formData.is_primary,
          config_param: domainConfig
        });

      if (updateError) throw updateError;

      if (!success) {
        throw new Error('Domaine non trouvé');
      }

      setSuccess(`Domaine ${formData.domain_name} mis à jour avec succès !`);
      
      // Notifier le parent
      onDomainUpdated();
      
      // Fermer après un délai
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err: any) {
      setError(`Erreur lors de la mise à jour: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !domain) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Edit3 className="w-5 h-5 mr-2" />
              Modifier le Domaine
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Modifiez les paramètres du domaine {domain.domain_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-700">{success}</span>
            </div>
          )}

          {/* Informations de base */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Informations de base</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de domaine *
              </label>
              <input
                type="text"
                name="domain_name"
                value={formData.domain_name}
                onChange={handleInputChange}
                placeholder="exemple.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_primary"
                id="is_primary"
                checked={formData.is_primary}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_primary" className="ml-2 block text-sm text-gray-700">
                Domaine principal pour ce tenant
              </label>
            </div>
          </div>

          {/* Personnalisation des couleurs */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Couleurs</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur primaire
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    name="primary_color"
                    value={formData.primary_color}
                    onChange={handleInputChange}
                    className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    name="primary_color"
                    value={formData.primary_color}
                    onChange={handleInputChange}
                    placeholder="#3B82F6"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur secondaire
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    name="secondary_color"
                    value={formData.secondary_color}
                    onChange={handleInputChange}
                    className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    name="secondary_color"
                    value={formData.secondary_color}
                    onChange={handleInputChange}
                    placeholder="#6B7280"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Médias */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Médias</h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL du logo
                </label>
                <input
                  type="url"
                  name="logo_url"
                  value={formData.logo_url}
                  onChange={handleInputChange}
                  placeholder="https://exemple.com/logo.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de l'icône
                </label>
                <input
                  type="url"
                  name="icon_url"
                  value={formData.icon_url}
                  onChange={handleInputChange}
                  placeholder="https://exemple.com/icon.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de l'image de fond
                </label>
                <input
                  type="url"
                  name="bg_url"
                  value={formData.bg_url}
                  onChange={handleInputChange}
                  placeholder="https://exemple.com/background.jpg"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Informations de contact */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Contact</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+33 1 23 45 67 89"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="contact@exemple.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="123 Rue de la Paix, 75001 Paris"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Mise à jour...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Mettre à jour
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
